import type { BillingInvoice } from './types';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(parseError(body) ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

function parseError(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as { error?: string };
    return parsed.error ?? null;
  } catch {
    return null;
  }
}

export async function fetchInvoices(params: {
  status?: string[];
  invoiceType?: string[];
  clientId?: string;
}): Promise<BillingInvoice[]> {
  const url = new URL('/api/billing/invoices', globalThis.location.origin);
  for (const s of params.status ?? []) url.searchParams.append('status', s);
  for (const t of params.invoiceType ?? []) url.searchParams.append('invoice_type', t);
  if (params.clientId) url.searchParams.set('client_id', params.clientId);
  const { rows } = await request<{ rows: BillingInvoice[] }>(url.pathname + url.search);
  return rows;
}

export function approveInvoice(id: string) {
  return request<{ invoice: BillingInvoice }>(`/api/billing/invoices/${id}/approve`, {
    method: 'POST',
  });
}

export function postponeInvoice(
  id: string,
  body: {
    mode: 'new_trigger_task' | 'future_date';
    reason: string;
    trigger_task_id?: string;
    until_date?: string;
  },
) {
  return request<{ invoice: BillingInvoice }>(`/api/billing/invoices/${id}/postpone`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function cancelInvoice(id: string, reason?: string) {
  return request<{ invoice: BillingInvoice }>(`/api/billing/invoices/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

export function markSent(id: string) {
  return request<{ invoice: BillingInvoice }>(`/api/billing/invoices/${id}/mark-sent`, {
    method: 'POST',
  });
}

export function approveAndSend(id: string) {
  return request<{ invoice: BillingInvoice }>(`/api/billing/invoices/${id}/approve-and-send`, {
    method: 'POST',
  });
}

export function editInvoice(
  id: string,
  patch: { amount?: string; description?: string | null; email_custom_text?: string | null },
) {
  return request<{ invoice: BillingInvoice }>(`/api/billing/invoices/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function replaceLineItems(
  id: string,
  lines: Array<{
    description: string;
    amount: string;
    quantity?: string;
    source_type?: 'project_rate' | 'subscription' | 'ad_hoc' | 'manual';
    source_id?: string | null;
  }>,
) {
  return request<{ lines: BillingInvoice['line_items'] }>(
    `/api/billing/invoices/${id}/line-items`,
    {
      method: 'PUT',
      body: JSON.stringify({ lines }),
    },
  );
}

export function approveGroup(clientId: string) {
  return request<{ approved: number }>(`/api/billing/groups/${clientId}/approve-all`, {
    method: 'POST',
  });
}

export function csvExportUrl(ids?: string[]): string {
  const url = new URL('/api/billing/export.csv', globalThis.location.origin);
  for (const id of ids ?? []) url.searchParams.append('ids', id);
  return url.pathname + url.search;
}
